/**
 * SITE CONSTRAINTS ON THE PROJECT PAGE (design hand-off "Site checks",
 * 15/10/2026 — README §1 items 1, 2, 3, 5, the narrow-screen rule, §2 and the
 * ten block states). This file is the CONTROLLER: it reads getSiteChecks,
 * re-runs runSiteChecks, records notes and task links through
 * annotateSiteFlag, opens the pin overlay and the detail panel, and paints
 * through the pure builders:
 *   project-detail-parts.js  siteCheckHtml / siteFlagsHtml / siteMastHtml /
 *                            siteRowExtraHtml / siteActivityItem
 *   site-checks-detail.js    the detail panel body
 *   site-checks-ui.js        the shared visual language
 *
 * THREE RULES THIS FILE KEEPS (unchanged from Phase 1).
 *   1. INDICATIVE ONLY — every surface says so; a clean site is one grey line.
 *   2. HOSTILE DATA — every string from a snapshot is escaped by the builders.
 *   3. NOTHING INVENTED — the server decides who may re-run (the control is only
 *      HIDDEN here), whether a run is skipped, and owns the snapshot's shape.
 *
 * The data is read AFTER the page's first paint; a slow or absent function
 * costs the block its own line and nothing else on the page.
 */
import { callFunction } from '../db.js';
import { escapeHtml } from '../ui.js';
import { openPanel } from './dialog.js';
import * as PX from './project-detail-parts.js';
import { siteDetailHtml, detailSubtitleHtml } from './site-checks-detail.js';
import { sortedFlags, tierOf, SOURCE_ORDER } from './site-checks-ui.js';

/** Who sees Re-run / Run checks. The server enforces the same list. */
export const RERUN_ROLES = ['Owner', 'Director', 'Coordinator', 'Engineer'];

/** The project has somewhere to check. */
export const hasSiteLocation = (project) => !!(project && (
  (project.siteAddress && (project.siteAddress.line1 || project.siteAddress.postcode)) || project.site
  || (project.siteLat != null && project.siteLng != null)));

// Open/closed survives a re-render of the page (an edit re-renders the whole
// record), the same way Document control keeps its panel across repaints.
let OPEN = false;

const plain = (v) => {
  if (v == null || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'object') return v.present === false ? 'none mapped' : (v.name || v.description || '—');
  return String(v);
};
const fmtDMY = (v) => { const d = new Date(v); return isNaN(d) ? 'an unknown date' : d.toLocaleDateString('en-GB'); };

// ── The snapshot as ONE record line for Ask Track (brief §9) ─────────────────
/**
 * Read-only and marked indicative in its own words, so an answer built on it
 * carries the caveat with it. Shaped like a golden-thread node (id, kind, at,
 * eyebrow, title, meta, byName) so it travels in `thread.nodes` unchanged.
 */
export function siteCheckEvidence(snap) {
  if (!snap || !snap.results) return null;
  const r = snap.results;
  const bits = [];
  const flags = sortedFlags(snap).map((f) => f.title).filter(Boolean);
  bits.push(flags.length ? `flags: ${flags.join('; ')}` : 'no flags raised');
  if (r.radon) bits.push(r.radon.status === 'ok' ? `radon class ${plain(r.radon.class)}${r.radon.percentRange ? ` (${plain(r.radon.percentRange)})` : ''}` : `radon ${r.radon.status}`);
  if (r.flood) bits.push(r.flood.status === 'ok' ? `flood zone ${plain(r.flood.zone)} (${plain(r.flood.source)})` : `flood ${r.flood.status}`);
  if (r.coal) bits.push(r.coal.status === 'ok'
    ? `coal: ${r.coal.inReportingArea ? 'in' : 'not in'} a coal mining reporting area, ${r.coal.inDevelopmentHighRiskArea ? 'IN' : 'not in'} a development high risk area`
    : `coal ${r.coal.status}`);
  if (r.geology) bits.push(r.geology.status === 'ok'
    ? `geology: bedrock ${plain(r.geology.bedrock)}; superficial ${plain(r.geology.superficial)}; artificial ground ${plain(r.geology.artificialGround)}; mass movement ${plain(r.geology.massMovement)}`
    : `geology ${r.geology.status}`);
  if (r.planning) {
    const n = Object.values(r.planning.byDataset || {}).reduce((s, list) => s + (Array.isArray(list) ? list.length : 0), 0);
    bits.push(r.planning.status === 'ok'
      ? `planning: ${n} designation${n === 1 ? '' : 's'} at the site, ${(r.planning.nearbyListed || []).length} listed building${(r.planning.nearbyListed || []).length === 1 ? '' : 's'} nearby (coverage varies by council)`
      : `planning ${r.planning.status}`);
  }
  const where = { pin: 'dropped pin', address: 'saved address point', postcode: 'postcode centroid, approximate' }[tierOf(snap)];
  return {
    id: `site-check:${snap.createdAt || ''}`, kind: 'site-check', at: snap.createdAt || null,
    eyebrow: 'Site constraints', byName: 'Track',
    title: `Site constraints check (automated, indicative) on ${fmtDMY(snap.createdAt)} (${where}): ${bits.join('; ')}.`,
    meta: 'Automated desk check against public datasets — indicative only, not a site investigation or a formal search.',
  };
}

function toast(msg) {
  const t = document.createElement('div');
  t.className = 'inline-toast'; t.setAttribute('role', 'status'); t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => t.remove(), 3200);
}

const MENU_ICON = {
  task: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="2" width="12" height="12" rx="2.5"/><path d="m5 8.2 2 2 4-4.4"/></svg>',
  fee: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 1.8h5.5L12.5 5v9.2H4Z"/><path d="M9.2 1.8V5h3.3"/></svg>',
  note: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" aria-hidden="true"><path d="M2.5 3h11v7.5H7.5L4.5 13v-2.5h-2Z"/></svg>',
};

/**
 * Mount the block and everything that reads the same snapshot.
 *
 * opts: {
 *   page            the project page root (delegated clicks for the masthead + Site row)
 *   host            #pdx-sc — the block (row + open panel) lives inside
 *   mastHost        span in .pdx-sub for the worst Warning
 *   siteExtraHost   span after the Site row's address (badge + pin link)
 *   projectId, project, session
 *   enquiryId       the linked enquiry's id, or null (fee notes live there)
 *   onOpenEnquiry(id), onAddAddress(), onTask({ title, description, sourceRef }) → Promise<{ id, assigneeName }|null>
 *   onLoaded(latest)   Ask Track evidence + the activity feed
 *   enquiryOwnerId  set when the block sits ON an enquiry (Karan 16/10/2026: "the
 *                   same as per projects"): it reads, re-runs and annotates that
 *                   enquiry's own checks; projectId is then unused.
 *   onFeeNote(flagId)  on an enquiry, "Add to fee proposal notes" adds the flag's
 *                   suggested sentence to that enquiry's notes (the caller owns them).
 * }
 */
export function mountSiteConstraints(opts) {
  const { page, host, mastHost, siteExtraHost, projectId, project, session, enquiryId = null, onOpenEnquiry, onAddAddress, onTask, onLoaded, enquiryOwnerId = null, onFeeNote } = opts;
  // Whose checks these are: a project's, or (on the enquiry page) the enquiry's own.
  const owner = enquiryOwnerId ? { enquiryId: enquiryOwnerId } : { projectId };
  const ownerPath = enquiryOwnerId ? `enquiries/${enquiryOwnerId}` : `projects/${projectId}`;
  if (!host) return null;
  const canRerun = !!(session && Array.isArray(session.roles) && session.roles.some((r) => RERUN_ROLES.includes(r)));
  const st = {
    latest: null, history: [], loaded: false, error: null,
    running: false, runningWhy: 'manual', noteFor: null, busyFlag: null, done: {},
  };
  const located = () => hasSiteLocation(project) || !!st.latest;

  const paint = () => {
    if (!host.isConnected) return;
    const keepNote = host.querySelector('.scb-note-in')?.value || '';
    let row;
    if (!st.loaded && !st.running) {
      row = `<div class="pdx-dc scb-block is-static" id="pdx-sc-row" aria-busy="true"><div class="pdx-dc-head"><span class="pdx-eyebrow">Site constraints</span><span class="pdx-flex"></span><span class="pdx-dc-verdict" style="color:var(--ink-3);">Reading…</span></div><div class="pdx-dc-line"><span class="fm-skel scb-sk" aria-hidden="true"></span></div></div>`;
    } else if (st.error && !st.latest) {
      row = `<div class="pdx-dc scb-block is-static" id="pdx-sc-row"><div class="pdx-dc-head"><span class="pdx-eyebrow">Site constraints</span><span class="pdx-flex"></span><span class="pdx-dc-verdict" style="color:var(--ink-3);">Not read</span></div><div class="pdx-dc-line">${escapeHtml(`Could not read the site check — ${st.error}`)}</div></div>`;
    } else {
      row = PX.siteCheckHtml(st.latest, { open: OPEN, running: st.running, runningWhy: st.runningWhy, located: located(), canRerun });
    }
    const showOpen = OPEN && st.latest && !st.running;
    host.innerHTML = `${row}<div id="pdx-sc-open" class="pdx-dc-open scb-open"${showOpen ? '' : ' hidden'}>${showOpen ? PX.siteFlagsHtml(st.latest, { noteFor: st.noteFor, done: st.done, canRerun, canAnnotate: true, busyFlag: st.busyFlag }) : ''}</div>`;
    const inp = host.querySelector('.scb-note-in');
    if (inp) { inp.value = keepNote; if (!st.busyFlag) inp.focus(); }
    if (mastHost) mastHost.innerHTML = st.running ? '' : PX.siteMastHtml(st.latest);
    if (siteExtraHost) siteExtraHost.innerHTML = PX.siteRowExtraHtml(st.latest, project);
  };

  const apply = (snap, { replaceHistory = null } = {}) => {
    if (replaceHistory) st.history = replaceHistory;
    else if (snap && !st.history.some((h) => h.id && h.id === snap.id)) st.history = [snap, ...st.history];
    st.latest = snap || null;
    if (typeof onLoaded === 'function') onLoaded(st.latest);
  };

  const load = async () => {
    try {
      const res = await callFunction('getSiteChecks', { ...owner, limit: 12 });
      apply((res && res.latest) || null, { replaceHistory: Array.isArray(res && res.history) ? res.history : [] });
      st.error = null;
    } catch (e) {
      st.error = (e && e.message) || String(e);
      if (typeof onLoaded === 'function') onLoaded(st.latest);
    }
    st.loaded = true;
    paint();
  };

  const rerun = async (why = 'manual', pin = null) => {
    if (st.running || !canRerun) return;
    st.running = true; st.runningWhy = why; st.noteFor = null;
    paint();
    try {
      const res = await callFunction('runSiteChecks', pin ? { ...owner, pin } : { ...owner });
      st.running = false;
      if (res && res.skipped) toast('Already checked within 24 hours — showing that check');
      if (res && res.snapshot) apply(res.snapshot);
      paint();
      if (!(res && res.skipped)) load(); // the fresh history (and the diff the server worked out)
    } catch (e) {
      st.running = false;
      paint();
      toast(`Re-run failed — ${(e && e.message) || e}`);
    }
  };

  const openPin = async () => {
    let mod = null;
    try { mod = await import('./site-pin.js'); } catch (e) { mod = null; }
    if (!mod || typeof mod.openSitePin !== 'function') { toast('Pin drop is not available yet'); return; }
    mod.openSitePin({
      ...owner, project, snapshot: st.latest,
      // Confirm closes the overlay at once: show "Re-checking…" with the previous results dimmed until the new ones land.
      onRunning: () => { st.running = true; st.runningWhy = 'pin'; paint(); },
      onError: (e) => { st.running = false; paint(); toast(`Pin not saved — ${(e && e.message) || e}`); },
      onConfirmed: (snap) => {
        st.running = false;
        if (snap) { apply(snap); paint(); load(); }
        else { st.running = true; st.runningWhy = 'pin'; paint(); load().finally(() => { st.running = false; paint(); }); }
      },
    });
  };

  // Open / close with motion (Karan 16/10/2026: "snappy, but intentional — how the
  // items below move down too"). The panel's HEIGHT animates, so everything under
  // the block slides with it instead of jumping; the flag rows rise in just behind.
  // --dur-base on --ease-out, the house motion; reduced motion → instant.
  const reduced = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let animating = null;
  const setOpen = (v) => {
    const next = !!v;
    if (next === OPEN && !animating) return;
    if (animating) { animating(); animating = null; }
    const toggle = host.querySelector('.scb-toggle');
    if (toggle) toggle.setAttribute('aria-expanded', String(next));   // the chevron turns at once
    if (reduced() || !st.latest || st.running) { OPEN = next; paint(); return; }
    const px = (n) => `${n}px`;
    if (next) {
      OPEN = true; paint();
      const p = host.querySelector('#pdx-sc-open');
      if (!p) return;
      // The panel tucks under the row with a negative top margin — animate it too, or the page hops at the end.
      const cs = getComputedStyle(p), h = p.offsetHeight, pt = cs.paddingTop, pb = cs.paddingBottom, mt = cs.marginTop;
      Object.assign(p.style, { overflow: 'hidden', height: '0px', paddingTop: '0px', paddingBottom: '0px', marginTop: '0px', opacity: '0' });
      p.classList.add('scb-anim', 'scb-in');
      void p.offsetHeight;   // commit the start frame
      Object.assign(p.style, { height: px(h), paddingTop: pt, paddingBottom: pb, marginTop: mt, opacity: '1' });
      const done = () => { Object.assign(p.style, { overflow: '', height: '', paddingTop: '', paddingBottom: '', marginTop: '', opacity: '' }); p.classList.remove('scb-anim'); setTimeout(() => p.classList.remove('scb-in'), 260); p.removeEventListener('transitionend', onEnd); animating = null; };
      const onEnd = (e) => { if (e.target === p && e.propertyName === 'height') done(); };
      p.addEventListener('transitionend', onEnd);
      const t = setTimeout(done, 450);
      animating = () => { clearTimeout(t); done(); };
    } else {
      const p = host.querySelector('#pdx-sc-open');
      if (!p || p.hidden) { OPEN = false; paint(); return; }
      Object.assign(p.style, { overflow: 'hidden', height: px(p.offsetHeight) });
      p.classList.add('scb-anim', 'scb-out');
      void p.offsetHeight;
      Object.assign(p.style, { height: '0px', paddingTop: '0px', paddingBottom: '0px', marginTop: '0px', opacity: '0' });
      const done = () => { p.removeEventListener('transitionend', onEnd); animating = null; OPEN = false; paint(); };
      const onEnd = (e) => { if (e.target === p && e.propertyName === 'height') done(); };
      p.addEventListener('transitionend', onEnd);
      const t = setTimeout(done, 400);
      animating = () => { clearTimeout(t); done(); };
    }
  };

  // ── The detail panel (§2) ────────────────────────────────────────────────
  const openDrawer = (atSource = null) => {
    const snap = st.latest;
    if (!snap) return;
    const addr = (snap.input && snap.input.address) || (project.siteAddress && project.siteAddress.line1) || project.site || 'Site';
    const d = openPanel({
      view: true,
      eyebrow: `Site constraints · ${project.number || ''}`.replace(/ · $/, ''),
      title: addr,
      subtitleHtml: detailSubtitleHtml(snap),
      note: snap.id ? `snapshot ${snap.id}` : '',
      render(body) {
        body.classList.add('scb-d-body');
        body.innerHTML = siteDetailHtml(snap, { history: st.history });
      },
    });
    const { body, foot, close } = d;
    const spacer = foot.querySelector('.tf-spacer');
    const tier = tierOf(snap);
    const pinBtn = document.createElement('button');
    pinBtn.type = 'button'; pinBtn.className = 'pdx-pill scb-foot-pill'; pinBtn.textContent = tier === 'pin' ? 'Move pin' : 'Drop a pin';
    pinBtn.addEventListener('click', () => { close(); openPin(); });
    foot.insertBefore(pinBtn, spacer);
    if (canRerun) {
      const rr = document.createElement('button');
      rr.type = 'button'; rr.className = 'pdx-pill scb-foot-pill'; rr.textContent = 'Re-run';
      rr.addEventListener('click', () => { close(); rerun('manual'); });
      foot.insertBefore(rr, spacer);
    }
    // Tabs deep-link to their section; the active tab follows the scroll.
    const tabs = [...body.querySelectorAll('[data-sc-tab]')];
    const mark = (k) => tabs.forEach((t) => { const on = t.dataset.scTab === k; t.classList.toggle('active', on); if (on) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current'); });
    const goTo = (k, smooth = true) => {
      const sec = body.querySelector(`[data-sc-sec="${k}"]`);
      const strip = body.querySelector('.scb-tabs');
      if (!sec) return;
      body.scrollTo({ top: sec.offsetTop - (strip ? strip.offsetHeight : 0) - 8, behavior: smooth ? 'smooth' : 'auto' });
      mark(k);
    };
    body.addEventListener('click', (e) => {
      const t = e.target.closest('[data-sc-tab]');
      if (t) { goTo(t.dataset.scTab); return; }
      if (e.target.closest('[data-sc="pin"]')) { close(); openPin(); }
    });
    body.addEventListener('scroll', () => {
      const strip = body.querySelector('.scb-tabs');
      const y = body.scrollTop + (strip ? strip.offsetHeight : 0) + 24;
      let cur = SOURCE_ORDER[0];
      for (const k of SOURCE_ORDER) { const s = body.querySelector(`[data-sc-sec="${k}"]`); if (s && s.offsetTop <= y) cur = k; }
      mark(cur);
    }, { passive: true });
    if (atSource && SOURCE_ORDER.includes(atSource)) requestAnimationFrame(() => setTimeout(() => goTo(atSource, false), 60));
  };

  // ── The ⋯ menu on a flag (state 5) ───────────────────────────────────────
  const flagById = (id) => (st.latest && Array.isArray(st.latest.flags) ? st.latest.flags.find((f) => String(f.id) === id) : null);
  const annotate = async (flagId, payload) => {
    const res = await callFunction('annotateSiteFlag', { ...owner, checkId: st.latest.id, flagId, ...payload });
    const f = flagById(flagId);
    if (res && res.flag && f) Object.assign(f, res.flag);
    return res;
  };
  const addTask = async (flagId) => {
    const f = flagById(flagId);
    if (!f || typeof onTask !== 'function') return;
    const made = await onTask({
      title: `${f.title}${f.action ? ` — ${String(f.action).split(/(?<=\.)\s/)[0]}` : ''}`,
      description: `From the site check of ${fmtDMY(st.latest.createdAt)} (indicative, public data).${f.action ? `\n${f.action}` : ''}`,
      sourceRef: `${ownerPath}/siteChecks/${st.latest.id || ''}#${flagId}`,
    }).catch((e) => { toast(`Task not created — ${(e && e.message) || e}`); return null; });
    if (!made) return;
    st.done[flagId] = { task: made.assigneeName ? String(made.assigneeName).split(/\s+/)[0] : '' };
    paint();
    try { await annotate(flagId, { taskRef: `tasks/${made.id}` }); paint(); }
    catch (e) { toast('Task created; the flag could not be linked to it'); }
  };
  const openMenu = (btn) => {
    document.querySelector('.pdx-menu')?.remove();
    const flagId = btn.dataset.flag;
    const menu = document.createElement('div');
    menu.className = 'pdx-menu scb-menu';
    menu.setAttribute('role', 'menu');
    const feeHere = typeof onFeeNote === 'function';   // on the enquiry itself
    menu.innerHTML = `${typeof onTask === 'function' ? `<button type="button" role="menuitem" data-k="task">${MENU_ICON.task}Add to tasks</button>` : ''}
      <button type="button" role="menuitem" data-k="fee"${feeHere || enquiryId ? '' : ' disabled aria-disabled="true" title="Fee notes live on the enquiry"'}>${MENU_ICON.fee}Add to fee proposal notes</button>
      <button type="button" role="menuitem" data-k="note">${MENU_ICON.note}Add a note</button>`;
    const r = btn.getBoundingClientRect();
    menu.style.top = `${Math.round(r.bottom + window.scrollY + 6)}px`;
    menu.style.left = `${Math.round(Math.max(12, Math.min(window.innerWidth - 252, r.right + window.scrollX - 240)))}px`;
    document.body.appendChild(menu);
    const close = () => { menu.remove(); document.removeEventListener('click', onDoc, true); document.removeEventListener('keydown', onKey, true); };
    const onDoc = (e) => { if (!menu.contains(e.target)) close(); };
    const onKey = (e) => { if (e.key === 'Escape') { close(); btn.focus(); } };
    setTimeout(() => { document.addEventListener('click', onDoc, true); document.addEventListener('keydown', onKey, true); }, 0);
    menu.addEventListener('click', (e) => {
      const b = e.target.closest('[data-k]');
      if (!b || b.disabled) return;
      close();
      if (b.dataset.k === 'task') addTask(flagId);
      else if (b.dataset.k === 'fee') { if (feeHere) onFeeNote(flagId); else if (enquiryId && onOpenEnquiry) onOpenEnquiry(enquiryId); }
      else if (b.dataset.k === 'note') { st.noteFor = flagId; paint(); }
    });
    menu.querySelector('button:not([disabled])')?.focus();
  };
  const saveNote = async (flagId) => {
    const inp = host.querySelector('.scb-note-in');
    const text = String(inp && inp.value || '').trim();
    if (!text) { inp?.focus(); return; }
    st.busyFlag = flagId; paint();
    try {
      const res = await annotate(flagId, { note: text });
      const f = flagById(flagId);
      if (f && !(res && res.flag)) f.notes = [...(f.notes || []), { by: session.displayName || session.email, at: new Date().toISOString(), text }];
      st.noteFor = null;
    } catch (e) {
      toast(`Note not saved — ${(e && e.message) || e}`);
    }
    st.busyFlag = null;
    paint();
  };

  // ── One delegated listener on the page: the block, the masthead, the Site row.
  const onClick = (e) => {
    const t = e.target.closest('[data-sc], [data-site-source]');
    const inBlock = host.contains(e.target);
    if (!t) {
      // The collapsed row's line and conv toggle too (Document control does the same).
      if (inBlock && e.target.closest('#pdx-sc-row') && !e.target.closest('.is-static') && st.latest) setOpen(!OPEN);
      return;
    }
    if (!page.contains(t)) return;
    const k = t.dataset.sc;
    if (t.dataset.siteSource != null && (k === undefined || k === 'source')) {
      if (t.dataset.siteSource) { e.preventDefault(); openDrawer(t.dataset.siteSource); }
      return;
    }
    switch (k) {
      case 'toggle': setOpen(!OPEN); break;
      case 'mast': e.preventDefault(); setOpen(true); host.scrollIntoView({ behavior: 'smooth', block: 'start' }); host.querySelector('.scb-toggle')?.focus({ preventScroll: true }); break;
      case 'rerun': rerun('manual'); break;
      case 'pin': openPin(); break;
      case 'add-address': if (onAddAddress) onAddAddress(); break;
      case 'drawer': openDrawer(); break;
      case 'menu': e.stopPropagation(); openMenu(t); break;
      case 'note-save': saveNote(t.dataset.flag); break;
      case 'note-cancel': st.noteFor = null; paint(); break;
      default: break;
    }
  };
  const onKey = (e) => {
    if (e.key === 'Enter' && e.target.classList && e.target.classList.contains('scb-note-in')) { e.preventDefault(); saveNote(e.target.dataset.flag); }
    if (e.key === 'Escape' && e.target.classList && e.target.classList.contains('scb-note-in')) { st.noteFor = null; paint(); }
  };
  page.addEventListener('click', onClick);
  page.addEventListener('keydown', onKey);

  paint();
  // Arriving from a task ("Open site constraints →") lands on #pdx-sc: open the block and bring it into view.
  const fromAnchor = typeof location !== 'undefined' && location.hash === '#pdx-sc';
  Promise.resolve(load()).then(() => { if (fromAnchor && st.latest) { OPEN = true; paint(); host.scrollIntoView({ behavior: 'smooth', block: 'start' }); } }).catch(() => {});
  // Track summary chips (kinds site-task / site-fee-note) act on a flag through these.
  const feeNote = (flagId) => { if (typeof onFeeNote === 'function') onFeeNote(flagId); else if (enquiryId && onOpenEnquiry) onOpenEnquiry(enquiryId); else toast('Fee notes live on the enquiry — this project has none linked.'); };
  return { reload: load, rerun, openDrawer, addTask, feeNote, openPin, get latest() { return st.latest; } };
}
